import type { LucideIcon } from "lucide-react";
import { Children, isValidElement, type MouseEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../atoms/Button";
import { Dialog } from "../../atoms/Dialog";
import { cn } from "../../lib/utils";
import { ConfirmDialogBody } from "./ConfirmDialogBody";
import { ConfirmDialogContext } from "./ConfirmDialogContext";
import { ConfirmDialogTrigger } from "./ConfirmDialogTrigger";
import { CONFIRM_DIALOG_VARIANT_CONFIG, type ConfirmDialogVariant } from "./styles";
import { useConfirmDialogState } from "./useConfirmDialogState";

export interface ConfirmDialogProps {
  /** Omit, with `onOpenChange`, to let a `ConfirmDialog.Trigger` open the dialog instead. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Sets the default icon, its color, and the confirm button's color. `destructive` also supplies
   * delete-flavored default copy.
   * @default "default"
   */
  variant?: ConfirmDialogVariant;
  /** Replaces the variant's default icon (`Info`, `TriangleAlert`, or `OctagonAlert`). */
  icon?: LucideIcon;
  /** @default "Are you sure?" */
  title?: string;
  /** Defaults to a permanent-deletion warning for `destructive`, and to none otherwise. */
  description?: string;
  /** @default "Delete" for `destructive`, "Continue" otherwise */
  confirmText?: string;
  /** @default "Cancel" */
  cancelText?: string;
  /**
   * Runs when the user confirms. The dialog shows a loading state while a returned promise is
   * pending and closes once it resolves. If it throws or rejects, the dialog stays open and shows
   * `errorMessage`.
   */
  onConfirm: () => void | Promise<void>;
  /**
   * Runs when the user closes the dialog without confirming, through Cancel or Escape. It is not
   * called after a confirm or when the parent closes the dialog through `open`, so put
   * declined-only cleanup here rather than in `onOpenChange`, which fires for every close.
   */
  onCancel?: () => void;
  /** Shows the loading state for work the dialog isn't awaiting itself. */
  loading?: boolean;
  confirmDisabled?: boolean;
  /**
   * Shown when `onConfirm` fails. Pass translated copy; a thrown error's message is never shown
   * since it isn't guaranteed to be user-safe.
   * @default "Something went wrong. Please try again."
   */
  errorMessage?: string;
  confirmButtonTestId?: string;
  /** An optional `ConfirmDialog.Trigger` and `ConfirmDialog.Body`. */
  children?: ReactNode;
}

// The dialog is portaled, but React still bubbles its events up the component tree, into whatever
// clickable row or card the dialog is rendered inside.
const stopPropagation = (event: MouseEvent) => event.stopPropagation();

const isBody = (child: ReactNode) => isValidElement(child) && child.type === ConfirmDialogBody;

const ConfirmDialogRoot = ({
  open,
  onOpenChange,
  variant = "default",
  icon,
  title,
  description,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  loading = false,
  confirmDisabled = false,
  errorMessage,
  confirmButtonTestId,
  children,
}: ConfirmDialogProps) => {
  const { t } = useTranslation();
  const state = useConfirmDialogState({ open, onOpenChange, onConfirm, onCancel, loading });
  const { icon: defaultIcon, iconChipClassName, confirmColor } = CONFIRM_DIALOG_VARIANT_CONFIG[variant];
  const Icon = icon ?? defaultIcon;
  const isDestructive = variant === "destructive";
  const resolvedDescription =
    description ??
    (isDestructive ? t("This will permanently delete the item. This action cannot be undone.") : undefined);
  const childList = Children.toArray(children);

  return (
    <Dialog open={state.open} onOpenChange={state.handleUserOpenChange}>
      <ConfirmDialogContext.Provider value={{ openFromTrigger: () => state.handleUserOpenChange(true) }}>
        {childList.filter((child) => !isBody(child))}
      </ConfirmDialogContext.Provider>
      <Dialog.Content
        data-slot="confirm-dialog"
        role="alertdialog"
        showCloseButton={false}
        closeOnInteractOutside={false}
        className="sm:max-w-md"
        // Radix warns when no description is linked unless `aria-describedby` is explicitly unset.
        {...(resolvedDescription ? {} : { "aria-describedby": undefined })}
        onOpenAutoFocus={state.focusCancel}
        onEscapeKeyDown={state.blockEscapeWhileBusy}
        onClick={stopPropagation}
      >
        <Dialog.Header className="flex-row items-start gap-3 text-left">
          <span className={cn("flex size-9 shrink-0 items-center justify-center rounded-lg", iconChipClassName)}>
            <Icon className="size-5" aria-hidden />
          </span>
          <div className="flex min-h-9 min-w-0 flex-1 flex-col justify-center gap-2">
            <Dialog.Title className="leading-6">{title ?? t("Are you sure?")}</Dialog.Title>
            {resolvedDescription && <Dialog.Description>{resolvedDescription}</Dialog.Description>}
          </div>
        </Dialog.Header>
        {childList.filter(isBody)}
        {state.hasFailed && (
          <p role="alert" className="pl-12 text-sm text-destructive">
            {errorMessage ?? t("Something went wrong. Please try again.")}
          </p>
        )}
        <Dialog.Footer>
          <Dialog.Close asChild>
            <Button ref={state.cancelRef} variant="outline" disabled={state.isBusy}>
              {cancelText ?? t("Cancel")}
            </Button>
          </Dialog.Close>
          <Button
            color={confirmColor}
            loading={state.isBusy}
            disabled={confirmDisabled}
            data-testid={confirmButtonTestId}
            onClick={state.confirm}
          >
            {confirmText ?? (isDestructive ? t("Delete") : t("Continue"))}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
};

// Storybook's generated source and React DevTools print display names; each part sets its own to
// match how it is written at call sites (`<ConfirmDialog.Trigger>`, not `ConfirmDialogTrigger`).
ConfirmDialogRoot.displayName = "ConfirmDialog";

/**
 * Asks the user to confirm an action before it runs. Built on `Dialog` with alert-dialog
 * semantics: it is announced as `alertdialog`, initial focus lands on Cancel so Enter can't confirm
 * by accident, and an outside click doesn't dismiss it (Escape still does).
 *
 * ## Parts
 *
 * The props alone make a complete dialog: an icon, title, description, and Cancel and confirm
 * buttons. Both parts are optional, and you add one only when the dialog needs it.
 *
 * **`ConfirmDialog.Trigger`**: use it when one specific control asks for the confirmation, such
 * as a delete button on a row. The dialog then manages its own open state, so the call site needs
 * no `useState`. The trigger keeps its own markup and handlers, and its click doesn't reach a
 * clickable row or card around it. It can sit anywhere inside `ConfirmDialog`.
 *
 * Leave it out when something other than a click decides to ask: a dropdown menu item (selecting
 * it closes the menu, which would unmount a dialog nested inside), an unsaved-changes guard, or a
 * step in a longer flow. Pass `open` and `onOpenChange` instead.
 *
 * **`ConfirmDialog.Body`**: use it when confirming needs more than the description can say, such
 * as a required reason field (pair it with `confirmDisabled`), a summary of what will change, or a
 * list of affected items. It renders between the description and the buttons, lined up with the
 * title. Leave it out for a plain question. It must be a direct child of `ConfirmDialog`, which
 * moves it into the dialog; a Body wrapped in another component is not found.
 *
 * @example
 * ```tsx
 * // Opened by its own button
 * <ConfirmDialog variant="destructive" title={t("Delete template?")} onConfirm={() => remove(id)}>
 *   <ConfirmDialog.Trigger>
 *     <Button iconOnly variant="ghost" hoverLabel={t("Delete")}><Trash2 /></Button>
 *   </ConfirmDialog.Trigger>
 * </ConfirmDialog>
 *
 * // Opened by the caller, with extra content
 * <ConfirmDialog open={isCancelling} onOpenChange={setIsCancelling} variant="destructive"
 *   title={t("Cancel appointment")} confirmDisabled={!reason.trim()} onConfirm={cancel}>
 *   <ConfirmDialog.Body>
 *     <Label htmlFor="reason">{t("Reason for cancellation")}</Label>
 *     <Input id="reason" value={reason} onChange={(e) => setReason(e.target.value)} />
 *   </ConfirmDialog.Body>
 * </ConfirmDialog>
 * ```
 */
export const ConfirmDialog = Object.assign(ConfirmDialogRoot, {
  Trigger: ConfirmDialogTrigger,
  Body: ConfirmDialogBody,
});
